<script setup lang="ts">
import AddNewUserDrawer from '@/views/apps/user/AddNewUserDrawer.vue' // Now used for both Add and Edit
import ConfirmDialog from '@/components/dialogs/ConfirmDialog.vue'
import type { Role, User } from '@/types/user'
import { useAuthStore } from '@/stores/auth'
import { getRoleDisplayName } from '@/utils/roleUtils'
import { formatDate24h } from '@/utils/dateFormatter'
import { useErrorHandler } from '@/composables/useErrorHandler'
import { useCrud } from '@/composables/useCrud'
import { useMasterDataStore } from '@/stores/masterData'

// Define page meta for CASL permissions
definePage({
  meta: {
    action: 'manage',
    subject: 'User',
  },
})

// 👉 Store
const authStore = useAuthStore()
const { t } = useI18n()
const { handleError, handleSuccess } = useErrorHandler()
const masterDataStore = useMasterDataStore()

// CRUD operations using composable
const {
  create: createUser,
  update: updateUser,
  saving,
} = useCrud<User>('/users', {
  messages: {
    createSuccess: t('users.userCreatedSuccessfully'),
    updateSuccess: t('users.userUpdatedSuccessfully'),
  },
  // Don't close drawer here - let addOrUpdateUser handle it after avatar upload
})

// Status toggle CRUD
const { update: updateStatus, saving: toggleStatusLoading } = useCrud('/users', {
  messages: {
    updateSuccess: t('users.userStatusUpdatedSuccessfully'),
  },
  onSuccess: async () => {
    await fetchUsers()
    await loadUserStats()
  },
})

const searchQuery = ref('')
const selectedRole = ref()
const selectedStatus = ref()

// Data table options
const itemsPerPage = ref(10)
const page = ref(1)
const sortBy = ref()
const orderBy = ref()
const selectedRows = ref([])

const isUserDrawerVisible = ref(false)
const userToEdit = ref<User | null>(null)

// Update data table options
const updateOptions = (options: any) => {
  sortBy.value = options.sortBy[0]?.key
  orderBy.value = options.sortBy[0]?.order
}

// Headers
const headers = computed(() => [
  { title: t('users.fullName'), key: 'user', sortable: true },
  { title: t('users.role'), key: 'role.name', sortable: true },
  { title: t('users.status'), key: 'isActive', sortable: true },
  { title: t('users.onlineStatus'), key: 'onlineStatus', sortable: true },
  { title: t('common.createdAt'), key: 'createdAt', sortable: true },
  { title: t('users.actions'), key: 'actions', sortable: false },
])

// 👉 Fetching users
const { data: usersData, execute: fetchUsers, isFetching: loading } = useApi<any>(createUrl('/users', {
  query: {
    q: searchQuery,
    status: selectedStatus,
    role: selectedRole,
    itemsPerPage,
    page,

    // Don't send sortBy to backend if it's onlineStatus (handle in frontend)
    sortBy: sortBy.value === 'onlineStatus' ? undefined : sortBy,
    orderBy: sortBy.value === 'onlineStatus' ? undefined : orderBy,
  },
}))

// Handle frontend sorting for onlineStatus since it's a computed field
const users = computed((): User[] => {
  const userList = usersData.value?.data || []

  // If sorting by onlineStatus, handle it in frontend since it's a computed field
  if (sortBy.value === 'onlineStatus') {
    return [...userList].sort((a, b) => {
      const aOnline = a.isOnline ? 1 : 0
      const bOnline = b.isOnline ? 1 : 0

      if (orderBy.value === 'desc')
        return bOnline - aOnline
      else
        return aOnline - bOnline
    })
  }

  return userList
})

const totalUsers = computed(() => usersData.value?.total || 0)

// 👉 Fetching Roles for filter and drawer (using master data store)
const roles = ref<any[]>([])

const loadRoles = async () => {
  try {
    roles.value = await masterDataStore.fetchRoles()
  }
  catch (error) {
    console.error('Failed to load roles:', error)
    handleError(error, t('roles.error'))
  }
}

// Computed property for the filter dropdown
const roleFilterItems = computed(() => {
  return roles.value.map(role => ({
    title: getRoleDisplayName(role.name),
    value: role.name.toLowerCase(), // e.g., 'admin'
  }))
})

const status = computed(() => [
  { title: t('common.active'), value: 'active' },
  { title: t('common.inactive'), value: 'inactive' },
])

const resolveUserRoleVariant = (role: string) => {
  const roleLowerCase = role.toLowerCase()

  if (roleLowerCase === 'admin')
    return { color: 'primary', icon: 'tabler-crown' }
  if (roleLowerCase === 'manager')
    return { color: 'info', icon: 'tabler-chart-pie' }
  if (roleLowerCase === 'member')
    return { color: 'success', icon: 'tabler-user' }

  return { color: 'primary', icon: 'tabler-user' }
}

const resolveUserStatusVariant = (isActive: boolean) => {
  if (isActive)
    return 'success'
  else
    return 'secondary'
}

// 👉 Open Drawer for editing
const openEditDrawer = (user: User) => { // Renamed for clarity
  userToEdit.value = user
  isUserDrawerVisible.value = true
}

// 👉 Open Drawer for adding
const openAddDrawer = () => {
  userToEdit.value = null // Ensure we are in "add" mode
  isUserDrawerVisible.value = true
}

// 👉 Add or Update user (using useCrud composable)
const addOrUpdateUser = async (userData: any) => {
  try {
    let userId: number

    // Extract avatar file before sending to API
    const { avatarFile, ...userDataWithoutFile } = userData

    if (userToEdit.value) {
      // Update existing user
      await updateUser(userToEdit.value.id, userDataWithoutFile)
      userId = userToEdit.value.id
    }
    else {
      // Add new user
      const response = await createUser(userDataWithoutFile)
      
      // Get user ID from response
      userId = response?.id
      
      if (!userId) {
        console.error('Create user response:', response)
        throw new Error('Failed to get user ID from create response')
      }
    }

    // Upload avatar if file is provided
    if (avatarFile && userId) {
      const formData = new FormData()
      formData.append('avatar', avatarFile)

      const uploadResponse = await $api(`/users/${userId}/avatar`, {
        method: 'POST',
        body: formData,
      })

      if (uploadResponse) {
        handleSuccess(t('users.avatarUploadedSuccessfully'))
      }
    }

    // Refresh users list
    await fetchUsers()
    await loadUserStats()
    
    // Close drawer after everything is done
    isUserDrawerVisible.value = false
  }
  catch (error) {
    console.error('Error in addOrUpdateUser:', error)
    handleError(error, t('users.error'))
  }
}

// 👉 Delete user function removed - only status toggle needed

// 👉 Kick user session (force offline)
const kickUserSession = async (user: User) => {
  try {
    await $api(`/auth/kick-session/${user.id}`, {
      method: 'POST',
      body: JSON.stringify({
        newSessionId: `admin_kick_${Date.now()}`, // Generate unique session ID for kick
      }),
    })

    handleSuccess(t('users.userSessionKickedSuccessfully'))

    // Refetch User and update statistics
    await fetchUsers()
    await loadUserStats()
  }
  catch (error) {
    console.error('Failed to kick user session:', error)
    handleError(error, t('users.error'))
  }
}

// 👉 Confirm kick user session
const isKickDialogVisible = ref(false)
const userToKick = ref<User | null>(null)

const confirmKickUserSession = (user: User) => {
  userToKick.value = user
  isKickDialogVisible.value = true
}

const handleKickConfirm = (confirmed: boolean) => {
  if (confirmed && userToKick.value)
    kickUserSession(userToKick.value)

  isKickDialogVisible.value = false
  userToKick.value = null
}

// 👉 Update user status (using useCrud composable)
const updateUserStatus = async (user: User) => {
  try {
    const newStatus = !user.isActive

    await updateStatus(`${user.id}/status`, { isActive: newStatus })
  }
  catch (error) {
    console.error('Failed to update user status:', error)
    handleError(error, t('users.error'))
  }
}

// 👉 User Statistics
const userStats = ref({
  totalUsers: 0,
  activeUsers: 0,
  onlineUsers: 0,
  inactiveUsers: 0,
  offlineUsers: 0,
})

// 👉 Computed widget data based on real statistics
const widgetData = computed(() => [
  {
    title: t('users.totalUsers'),
    value: userStats.value.totalUsers.toString(),
    change: 0,
    desc: t('users.totalUsersDesc'),
    icon: 'tabler-users',
    iconColor: 'primary',
  },
  {
    title: t('users.onlineUsers'),
    value: `${userStats.value.onlineUsers} / ${userStats.value.offlineUsers}`,
    change: 0,
    desc: t('users.onlineUsersDesc'),
    icon: 'tabler-wifi',
    iconColor: 'success',
  },
  {
    title: t('users.activeUsers'),
    value: userStats.value.activeUsers.toString(),
    change: 0,
    desc: t('users.activeUsersDesc'),
    icon: 'tabler-user-check',
    iconColor: 'info',
  },
  {
    title: t('users.inactiveUsers'),
    value: userStats.value.inactiveUsers.toString(),
    change: 0,
    desc: t('users.inactiveUsersDesc'),
    icon: 'tabler-user-x',
    iconColor: 'warning',
  },
])

// 👉 Load user statistics
const loadUserStats = async () => {
  try {
    // ใช้ backend stats แทนการเช็ค socketId ใน frontend
    const { data } = await useApi('/users/stats')

    if (data.value) {
      userStats.value = {
        totalUsers: data.value.totalUsers,
        activeUsers: data.value.activeUsers,
        onlineUsers: data.value.onlineUsers,
        inactiveUsers: data.value.inactiveUsers,
        offlineUsers: data.value.offlineUsers,
      }
    }
  }
  catch (error) {
    console.error('Failed to load user statistics:', error)
  }
}

// 👉 Watch for changes in users data to update statistics
watch(users, () => {
  if (users.value.length > 0) {
    // ใช้ backend stats แทนการเช็ค socketId ใน frontend
    loadUserStats()
  }
}, { immediate: true })

// Event handlers for WebSocket events
const handleUserStatsUpdated = (event: CustomEvent) => {
  const stats = event.detail
  userStats.value = {
    totalUsers: stats.totalUsers,
    activeUsers: stats.activeUsers,
    onlineUsers: stats.onlineUsers,
    inactiveUsers: stats.inactiveUsers,
    offlineUsers: stats.offlineUsers,
  }
}

const handleUserStatusChanged = () => {
  fetchUsers() // Refresh users list
  loadUserStats() // Refresh statistics
}

// Load data on mount
onMounted(async () => {
  // Load initial data
  await Promise.all([
    loadRoles(),
    loadUserStats(),
  ])

  // Listen for real-time user statistics updates
  window.addEventListener('user_stats_updated', handleUserStatsUpdated)
  window.addEventListener('userStatusChanged', handleUserStatusChanged)
})

// Cleanup on unmount
onUnmounted(() => {
  window.removeEventListener('user_stats_updated', handleUserStatsUpdated)
  window.removeEventListener('userStatusChanged', handleUserStatusChanged)
})
</script>

<template>
  <section>
    <!-- 👉 Page Header -->
    <div class="mb-6">
      <h1 class="text-h4 font-weight-bold mb-2">
        {{ t('users.title') }}
      </h1>
      <p class="text-body-1 text-medium-emphasis">
        {{ t('users.subtitle') }}
      </p>
    </div>

    <!-- 👉 Widgets -->
    <div class="d-flex mb-6">
      <VRow>
        <template
          v-for="(data, id) in widgetData"
          :key="id"
        >
          <VCol
            cols="12"
            md="3"
            sm="6"
          >
            <VCard>
              <VCardText>
                <div class="d-flex justify-space-between">
                  <div class="d-flex flex-column gap-y-1">
                    <div class="text-body-1 text-high-emphasis">
                      {{ data.title }}
                    </div>
                    <div class="d-flex gap-x-2 align-center">
                      <template v-if="data.title === t('users.onlineUsers')">
                        <h4 class="text-h4">
                          {{ userStats.onlineUsers }}
                        </h4>
                        <VChip
                          v-if="userStats.onlineUsers > 0"
                          color="success"
                          size="small"
                          variant="tonal"
                          class="me-1"
                        >
                          <VIcon
                            icon="tabler-circle-filled"
                            size="8"
                            class="me-1"
                          />
                          {{ t('users.online') }}
                        </VChip>
                        <span class="text-h4 mx-1">/</span>
                        <h4 class="text-h4">
                          {{ userStats.offlineUsers }}
                        </h4>
                        <VChip
                          v-if="userStats.offlineUsers > 0"
                          color="error"
                          size="small"
                          variant="tonal"
                        >
                          <VIcon
                            icon="tabler-circle-filled"
                            size="8"
                            class="me-1"
                          />
                          {{ t('users.offline') }}
                        </VChip>
                      </template>
                      <template v-else>
                        <h4 class="text-h4">
                          {{ data.value }}
                        </h4>
                      </template>
                    </div>
                    <div class="text-sm">
                      {{ data.desc }}
                    </div>
                  </div>
                  <VAvatar
                    :color="data.iconColor"
                    variant="tonal"
                    rounded
                    size="42"
                  >
                    <VIcon
                      :icon="data.icon"
                      size="26"
                    />
                  </VAvatar>
                </div>
              </VCardText>
            </VCard>
          </VCol>
        </template>
      </VRow>
    </div>

    <VCard class="mb-6">
      <VCardItem class="pb-4">
        <VCardTitle>{{ t('common.filters') }}</VCardTitle>
      </VCardItem>

      <VCardText>
        <VRow>
          <!-- 👉 Select Role -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedRole"
              :placeholder="t('users.selectRole')"
              :items="roleFilterItems"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
          <!-- 👉 Select Status -->
          <VCol
            cols="12"
            sm="4"
          >
            <AppSelect
              v-model="selectedStatus"
              :placeholder="t('common.selectStatus')"
              :items="status"
              clearable
              clear-icon="tabler-x"
            />
          </VCol>
        </VRow>
      </VCardText>

      <VDivider />

      <VCardText class="d-flex flex-wrap gap-4">
        <div class="me-3 d-flex gap-3">
          <AppSelect
            :model-value="itemsPerPage"
            :items="[
              { value: 10, title: '10' },
              { value: 25, title: '25' },
              { value: 50, title: '50' },
              { value: 100, title: '100' },
              { value: -1, title: t('common.all') },
            ]"
            style="inline-size: 6.25rem;"
            @update:model-value="itemsPerPage = parseInt($event, 10)"
          />
        </div>
        <VSpacer />

        <div class="app-user-search-filter d-flex align-center flex-wrap gap-4">
          <!-- 👉 Search  -->
          <div style="inline-size: 15.625rem;">
            <AppTextField
              v-model="searchQuery"
              :placeholder="t('users.searchUser')"
            />
          </div>

          <!-- 👉 Add user button -->
          <VBtn
            v-if="$can('create', 'User')"
            prepend-icon="tabler-plus"
            @click="openAddDrawer"
          >
            {{ t('common.add') }} {{ t('users.user') }}
          </VBtn>
        </div>
      </VCardText>

      <VDivider />

      <!-- SECTION datatable -->
      <VDataTableServer
        v-model:items-per-page="itemsPerPage"
        v-model:model-value="selectedRows"
        v-model:page="page"
        :items="users"
        item-value="id"
        :items-length="totalUsers"
        :headers="headers"
        :loading="loading"
        class="text-no-wrap"
        @update:options="updateOptions"
      >
        <!-- User -->
        <template #item.user="{ item }">
          <div class="d-flex align-center gap-x-4">
            <VAvatar
              size="34"
              :variant="!item.avatar ? 'tonal' : undefined"
              :color="!item.avatar ? resolveUserRoleVariant(item.role.name).color : undefined"
            >
              <VImg
                v-if="item.avatar"
                :src="item.avatar"
              />
              <span v-else>{{ avatarText(`${item.firstName} ${item.lastName}`) }}</span>
            </VAvatar>
            <div class="d-flex flex-column">
              <h6 class="text-base">
                {{ item.firstName }} {{ item.lastName }}
              </h6>
              <div class="text-sm">
                {{ item.email }}
              </div>
            </div>
          </div>
        </template>

        <!-- 👉 Role -->
        <template #item.role.name="{ item }">
          <div class="d-flex align-center gap-x-2">
            <VIcon
              :size="22"
              :icon="resolveUserRoleVariant(item.role.name).icon"
              :color="resolveUserRoleVariant(item.role.name).color"
            />

            <div class="text-high-emphasis text-body-1">
              {{ getRoleDisplayName(item.role) }}
            </div>
          </div>
        </template>

        <!-- Status -->
        <template #item.isActive="{ item }">
          <VChip
            :color="resolveUserStatusVariant(item.isActive)"
            size="small"
            label
            class="text-capitalize"
          >
            {{ item.isActive ? t('common.active') : t('common.inactive') }}
          </VChip>
        </template>

        <!-- Online Status -->
        <template #item.onlineStatus="{ item }">
          <VChip
            :color="item.isOnline ? 'success' : 'error'"
            size="small"
            label
            class="text-capitalize"
          >
            <VIcon
              :icon="item.isOnline ? 'tabler-circle-filled' : 'tabler-circle'"
              size="8"
              class="me-1"
            />
            {{ item.isOnline ? t('users.online') : t('users.offline') }}
          </VChip>
        </template>

        <!-- Created At -->
        <template #item.createdAt="{ item }">
          <span class="text-sm">
            {{ formatDate24h(item.createdAt) }}
          </span>
        </template>

        <!-- Actions -->
        <template #item.actions="{ item }">
          <IconBtn
            v-if="$can('update', 'User')"
            @click="openEditDrawer(item)"
          >
            <VIcon icon="tabler-pencil" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'User') && item.id !== authStore.user?.id"
            @click="updateUserStatus(item)"
          >
            <VIcon :icon="item.isActive ? 'tabler-user-off' : 'tabler-user-check'" />
          </IconBtn>

          <IconBtn
            v-if="$can('update', 'User') && item.id !== authStore.user?.id && item.isOnline"
            @click="confirmKickUserSession(item)"
          >
            <VIcon icon="tabler-logout" />
          </IconBtn>
        </template>

        <!-- pagination -->
        <template #bottom>
          <TablePagination
            v-model:page="page"
            :items-per-page="itemsPerPage"
            :total-items="totalUsers"
          />
        </template>
      </VDataTableServer>
      <!-- SECTION -->
    </VCard>
    <!-- 👉 Add/Edit User Drawer -->
    <AddNewUserDrawer
      v-model:is-drawer-open="isUserDrawerVisible"
      :user="userToEdit"
      :roles="roles"
      @user-data="addOrUpdateUser"
    />

    <!-- 👉 Kick User Confirmation Dialog -->
    <ConfirmDialog
      v-model:is-dialog-visible="isKickDialogVisible"
      :confirmation-question="userToKick ? t('users.confirmKickUser', { name: `${userToKick.firstName} ${userToKick.lastName}` }) : ''"
      :confirm-title="t('users.kickUser')"
      :confirm-msg="t('users.kickUserMsg')"
      :cancel-title="t('common.cancel')"
      :cancel-msg="t('users.keepUserOnline')"
      @confirm="handleKickConfirm"
    />
  </section>
</template>
